import type { CaptureEdgeKind } from '@vivport/backend/types';
import { create } from 'zustand';

// One capture session = one observation: every round submitted from it joins the
// session's observation group, started silently on the first submit. There is no
// explicit end — the session is released when the chat resets or a new capture
// request replaces it, and groups have no status to close.
export interface CaptureSubmit {
  itemId: string;
  photoIds: number[];
  recordingIds: number[];
  videoIds: number[];
  linkContentId: number | null;
  edgeKind: CaptureEdgeKind | null;
  observationGroupId: number | null;
}

// Staged evidence lives here rather than in the capture component: the member swaps
// between evidence tools mid-session, and component state would be destroyed on every
// swap. The store also owns each item's object URL — revoked on remove, on a successful
// submit, and when the session ends — so nothing revokes a preview that is still shown.
export interface StagedItem {
  id: string;
  kind: 'photo' | 'recording' | 'video';
  file: File;
  url: string;
  durationSeconds?: number | null;
}

export interface CaptureRound {
  photoIds: number[];
  recordingIds: number[];
  videoIds: number[];
  linkContentId: number | null;
  edgeKind: CaptureEdgeKind | null;
}

interface CaptureFlowState {
  roundsByItemId: Record<string, CaptureRound[]>;
  observationGroupIdByItemId: Record<string, number>;
  stagedBySessionId: Record<string, StagedItem[]>;
  inFlightBySessionId: Record<string, StagedItem[]>;
  addStagedItems: (sessionId: string, items: StagedItem[]) => void;
  replaceStagedItem: (sessionId: string, itemId: string, next: StagedItem) => void;
  removeStagedItem: (sessionId: string, itemId: string) => void;
  beginSubmitting: (sessionId: string, itemIds: string[]) => void;
  retireInFlight: (sessionId: string, itemIds: string[]) => void;
  returnInFlight: (sessionId: string, itemIds: string[]) => void;
  endSession: (sessionId: string) => void;
  handler: ((payload: CaptureSubmit) => void) | null;
  registerHandler: (fn: ((payload: CaptureSubmit) => void) | null) => void;
  submitCapture: (payload: CaptureSubmit) => void;
  recordSessionObservationGroup: (sessionId: string, groupId: number) => void;
  setSessionObservationGroup: (sessionId: string, groupId: number | null) => void;
  reset: () => void;
}

export const useCaptureFlowStore = create<CaptureFlowState>((set, get) => ({
  roundsByItemId: {},
  observationGroupIdByItemId: {},
  stagedBySessionId: {},
  inFlightBySessionId: {},
  handler: null,

  addStagedItems: (sessionId, items) =>
    set((state) => ({
      stagedBySessionId: {
        ...state.stagedBySessionId,
        [sessionId]: [...(state.stagedBySessionId[sessionId] ?? []), ...items],
      },
    })),

  // Punch-in rewrites a take in place, so the edited recording keeps its position in the
  // tray and its identity in any in-flight submit.
  replaceStagedItem: (sessionId, itemId, next) =>
    set((state) => {
      const current = state.stagedBySessionId[sessionId] ?? [];
      const previous = current.find((item) => item.id === itemId);
      if (previous && previous.url !== next.url) URL.revokeObjectURL(previous.url);
      return {
        stagedBySessionId: {
          ...state.stagedBySessionId,
          [sessionId]: current.map((item) => (item.id === itemId ? next : item)),
        },
      };
    }),

  removeStagedItem: (sessionId, itemId) =>
    set((state) => {
      const current = state.stagedBySessionId[sessionId] ?? [];
      const target = current.find((item) => item.id === itemId);
      if (target) URL.revokeObjectURL(target.url);
      return {
        stagedBySessionId: {
          ...state.stagedBySessionId,
          [sessionId]: current.filter((item) => item.id !== itemId),
        },
      };
    }),

  // The tray empties the moment the member presses Send, but the evidence is not gone
  // until the server has it: these items move to a holding area with their object URLs
  // INTACT, because a failed upload has to be able to put them back with their previews
  // still working.
  beginSubmitting: (sessionId, itemIds) =>
    set((state) => {
      const moving = new Set(itemIds);
      const current = state.stagedBySessionId[sessionId] ?? [];
      return {
        stagedBySessionId: {
          ...state.stagedBySessionId,
          [sessionId]: current.filter((item) => !moving.has(item.id)),
        },
        inFlightBySessionId: {
          ...state.inFlightBySessionId,
          [sessionId]: [
            ...(state.inFlightBySessionId[sessionId] ?? []),
            ...current.filter((item) => moving.has(item.id)),
          ],
        },
      };
    }),

  // Created — this is the only place a submitted item's URL is revoked.
  retireInFlight: (sessionId, itemIds) =>
    set((state) => {
      const gone = new Set(itemIds);
      const current = state.inFlightBySessionId[sessionId] ?? [];
      current.forEach((item) => {
        if (gone.has(item.id)) URL.revokeObjectURL(item.url);
      });
      return {
        inFlightBySessionId: {
          ...state.inFlightBySessionId,
          [sessionId]: current.filter((item) => !gone.has(item.id)),
        },
      };
    }),

  // Failed — back to the front of the tray, preview intact, in capture order and ahead of
  // anything the member staged while the upload was running.
  returnInFlight: (sessionId, itemIds) =>
    set((state) => {
      const back = new Set(itemIds);
      const current = state.inFlightBySessionId[sessionId] ?? [];
      return {
        stagedBySessionId: {
          ...state.stagedBySessionId,
          [sessionId]: [
            ...current.filter((item) => back.has(item.id)),
            ...(state.stagedBySessionId[sessionId] ?? []),
          ],
        },
        inFlightBySessionId: {
          ...state.inFlightBySessionId,
          [sessionId]: current.filter((item) => !back.has(item.id)),
        },
      };
    }),

  endSession: (sessionId) =>
    set((state) => {
      [
        ...(state.stagedBySessionId[sessionId] ?? []),
        ...(state.inFlightBySessionId[sessionId] ?? []),
      ].forEach((item) => URL.revokeObjectURL(item.url));
      const nextStaged = { ...state.stagedBySessionId };
      const nextInFlight = { ...state.inFlightBySessionId };
      delete nextStaged[sessionId];
      delete nextInFlight[sessionId];
      return { stagedBySessionId: nextStaged, inFlightBySessionId: nextInFlight };
    }),

  registerHandler: (fn) => set({ handler: fn }),

  // The capture tool records the group its own submit used, so EVERY door that mounts the
  // tool keeps one session to one observation. Recording it from a host's submit callback
  // instead is what let the Explore dialog start a new observation on every submit.
  recordSessionObservationGroup: (sessionId, groupId) =>
    set((state) => ({
      observationGroupIdByItemId: { ...state.observationGroupIdByItemId, [sessionId]: groupId },
    })),

  // The member's choice of which Observation this session groups into. null means New:
  // the key is DELETED so observationGroupInput() falls back to { start: true }, which
  // keeps that construction in exactly one place.
  setSessionObservationGroup: (sessionId, groupId) =>
    set((state) => {
      const next = { ...state.observationGroupIdByItemId };
      if (groupId === null) delete next[sessionId];
      else next[sessionId] = groupId;
      return { observationGroupIdByItemId: next };
    }),

  // A round is evidence arriving, not the member finishing: the session stays live so
  // the same subject can gain another medium, and every round joins one Observation.
  submitCapture: (payload) => {
    set((state) => ({
      roundsByItemId: {
        ...state.roundsByItemId,
        [payload.itemId]: [
          ...(state.roundsByItemId[payload.itemId] ?? []),
          {
            photoIds: payload.photoIds,
            videoIds: payload.videoIds,
            recordingIds: payload.recordingIds,
            linkContentId: payload.linkContentId,
            edgeKind: payload.edgeKind,
          },
        ],
      },
    }));
    get().handler?.(payload);
  },
  reset: () =>
    set((state) => {
      [
        ...Object.values(state.stagedBySessionId),
        ...Object.values(state.inFlightBySessionId),
      ].forEach((items) => items.forEach((item) => URL.revokeObjectURL(item.url)));
      return {
        roundsByItemId: {},
        observationGroupIdByItemId: {},
        stagedBySessionId: {},
        inFlightBySessionId: {},
      };
    }),
}));
